import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { IoClose } from "react-icons/io5";
import { FiLoader } from "react-icons/fi";
import './campaignModal.css'
import {
  differenceInDays,
  differenceInMonths,
} from "date-fns";
import { createCampaign, fetchMyCommunities } from "../api";

const CampaignSetupModal = ({ 
  isOpen, 
  onClose,
  onSuccess
}) => {
  const [formValues, setFormValues] = useState({
    campaignType: "MANDATORY",
    title: "",
    description: "",
    communityId: "",
    endDate: "",
    amountPerUser: 1,
    totalAmountNeeded: 0,
    currency: "₹",
    coverImage: null,
    isFamilyHead: false,
    totalMembers: 0,
    schedule: "one_time",
    reminder: "one_time",
    taxes: [],
  });

  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [loader, setLoader] = useState(false);
  const [communityLoader, setCommunityLoader] = useState(false);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [communityOptions, setCommunityOptions] = useState([]);

  const DEFAULT_CAMPAIGN_IMAGE = "/campaign-demo-image.webp";

  // Tax type options
  const taxTypeOptions = [
    { value: 'gst', label: 'GST' },
    { value: 'vat', label: 'VAT' },
    { value: 'Professional_tax', label: 'Professional Tax' }
  ];

  // Fetch communities when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCommunities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Flatten communities for dropdown
  const flattenCommunities = (communities, level = 0) => {
    let flattened = [];
    
    communities.forEach(community => {
      // Add current community with level info
      flattened.push({
        ...community,
        level,
        displayName: `${level > 0 ? '↳ '.repeat(level) : ''}${community.name}`,
        hasSubCommunities: community.subCommunities && community.subCommunities.length > 0
      });
      
      // Recursively add subcommunities
      if (community.subCommunities && community.subCommunities.length > 0) {
        flattened = [...flattened, ...flattenCommunities(community.subCommunities, level + 1)];
      }
    });
    
    return flattened;
  };

  const fetchCommunities = async () => {
    try {
      setCommunityLoader(true);
      const data = await fetchMyCommunities();
      console.log("Fetched communities:", data);
      
      // Check the actual structure of the response
      let communities = [];
      if (Array.isArray(data)) {
        communities = data;
      } else if (data?.communities && Array.isArray(data.communities)) {
        communities = data.communities;
      }
      
      
      // Create flattened options for dropdown
      const flattened = flattenCommunities(communities);
      setCommunityOptions(flattened);
      
      // Auto-select first community if available
      if (flattened.length > 0) {
        const firstCommunity = flattened[0];
        await handleCommunityChange(firstCommunity);
      }
    } catch (error) {
      toast.error("Failed to load communities");
      console.error("Error fetching communities:", error);
    } finally {
      setCommunityLoader(false);
    }
  };

  // Calculate total amount needed when community or amount per user changes
  useEffect(() => {
    const members = formValues.totalMembers || 0;
    const amount = formValues.amountPerUser || 0;
  
    setFormValues((prev) => ({
      ...prev,
      totalAmountNeeded: members * amount,
    }));
  }, [formValues.amountPerUser, formValues.totalMembers]);

  // Handle tax type checkbox change
  const handleTaxTypeChange = (taxType) => {
    setFormValues(prev => {
      const currentTaxes = [...prev.taxes];
      const existingTaxIndex = currentTaxes.findIndex(tax => tax.type === taxType);
      
      if (existingTaxIndex >= 0) {
        // Remove tax if it exists
        currentTaxes.splice(existingTaxIndex, 1);
      } else {
        // Add new tax with default percentage
        currentTaxes.push({
          type: taxType,
          percentage: ""
        });
      }
      
      return {
        ...prev,
        taxes: currentTaxes
      };
    });
  };

  // Handle tax percentage change
  const handleTaxPercentageChange = (taxType, percentage) => {
    setFormValues(prev => {
      const updatedTaxes = prev.taxes.map(tax => 
        tax.type === taxType 
          ? { ...tax, percentage: percentage } 
          : tax
      );
      
      return {
        ...prev,
        taxes: updatedTaxes
      };
    });
  };

  // Check if a tax type is selected
  const isTaxTypeSelected = (taxType) => {
    return formValues.taxes.some(tax => tax.type === taxType);
  };

  // Get percentage for a specific tax type
  const getTaxPercentage = (taxType) => {
    const tax = formValues.taxes.find(tax => tax.type === taxType);
    return tax ? tax.percentage : "";
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      setFormValues(prev => ({
        ...prev,
        [name]: e.target.checked
      }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }

    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleCommunityChange = (community) => {
    console.log(community);
    
  
    setFormValues((prev) => ({
      ...prev,
      communityId: community.id,
      totalMembers: community.userCount || 0,
    }));
  
    setValidationErrors((prev) => ({ ...prev, communityId: "" }));
  };
  

  const handleImageUpload = (fieldName, file) => {
    if (fieldName === "coverImage") {
      setFormValues(prev => ({ ...prev, coverImage: file }));
      
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setCoverImagePreview(previewUrl);
      } else {
        setCoverImagePreview("");
      }
    }
  };

  const urlToFile = async (url, filename) => {
    const fullUrl = window.location.origin + url;
    const response = await fetch(fullUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formValues.title.trim()) errors.title = "Title is required.";
    if (!formValues.description.trim()) errors.description = "Description is required.";
    if (!formValues.communityId) errors.communityId = "Please select a community.";
    if (!formValues.endDate) errors.endDate = "End date is required.";
    if (formValues.amountPerUser <= 0) {
      errors.amountPerUser = "Amount per user must be greater than 0.";
    }
    
    // Validate taxes - each selected tax must have a percentage
    formValues.taxes.forEach(tax => {
      if (!tax.percentage || isNaN(tax.percentage) || tax.percentage <= 0) {
        errors[`tax_${tax.type}`] = `${tax.type.toUpperCase()} percentage must be greater than 0`;
      }
    });
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const formData = new FormData();
    formData.append('campaignType', formValues.campaignType);
    formData.append('title', formValues.title);
    formData.append('description', formValues.description);
    formData.append('communityId', formValues.communityId);
    formData.append('endDate', formValues.endDate);
    formData.append('amountPerUser', formValues.amountPerUser.toString());
    formData.append('totalAmountNeeded', formValues.totalAmountNeeded.toString());
    formData.append('currency', formValues.currency);
    formData.append('isFamilyHead', formValues.isFamilyHead.toString());
    formData.append('totalMembers', formValues.totalMembers.toString());
    formData.append('schedule', formValues.schedule);
    formData.append('reminder', formValues.reminder);
    
    // Append taxes as JSON string
    if (formValues.taxes.length > 0) {
      formData.append('taxes', JSON.stringify(formValues.taxes));
    }
    
    // Append cover image if exists
    if (formValues.coverImage) {
      formData.append('coverImage', formValues.coverImage);
    } else {
      try {
        const defaultImageFile = await urlToFile(DEFAULT_CAMPAIGN_IMAGE, 'default-campaign-image.webp');
        formData.append('coverImage', defaultImageFile);
      } catch (error) {
        console.error('Failed to load default image:', error);
        const placeholderBlob = new Blob([], { type: 'image/webp' });
        const placeholderFile = new File([placeholderBlob], 'placeholder.webp');
        formData.append('coverImage', placeholderFile);
      }
    }

    try {
      setLoader(true);
      await createCampaign(formData);
      toast.success("Campaign created successfully");
      
      // Reset form and close modal
      resetForm();
      onClose();
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong, try again later.");
    } finally {
      setLoader(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      campaignType: "MANDATORY",
      title: "",
      description: "",
      communityId: "",
      endDate: "",
      amountPerUser: 0,
      totalAmountNeeded: 0,
      currency: "₹",
      coverImage: null,
      isFamilyHead: false,
      totalMembers: 0,
      schedule: "one_time",
      reminder: "one_time",
      taxes: [],
    });
    setCoverImagePreview("");
    setCommunityOptions([]);
    setValidationErrors({});
  };

  const getScheduleOptions = (endDate) => {
    if (!endDate) return [];
    const currentDate = new Date();
    const endDateObj = new Date(endDate);

    const daysDifference = differenceInDays(endDateObj, currentDate);
    const monthsDifference = differenceInMonths(endDateObj, currentDate);

    if (daysDifference <= 1) {
      return [{ value: "one_time", label: "Single Use" }];
    } else if (daysDifference <= 7) {
      return [
        { value: "one_time", label: "Single Use" },
        { value: "daily", label: "Daily" },
      ];
    } else if (monthsDifference <= 1) {
      return [
        { value: "one_time", label: "Single Use" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
      ];
    } else if (monthsDifference <= 6) {
      return [
        { value: "one_time", label: "Single Use" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
      ];
    } else if (monthsDifference <= 12) {
      return [
        { value: "one_time", label: "Single Use" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "half_yearly", label: "Half Yearly" },
      ];
    } else {
      return [
        { value: "one_time", label: "Single Use" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "half_yearly", label: "Half Yearly" },
        { value: "quarterly", label: "Quarterly (3 months)" },
        { value: "yearly", label: "Yearly" },
      ];
    }
  };

  useEffect(() => {
    setScheduleOptions(getScheduleOptions(formValues.endDate));
  }, [formValues.endDate]);

  // Custom ImageUpload component
  const ImageUpload = ({ label, fieldName, value, onChange }) => {
    const handleImageChange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        onChange(fieldName, file);
      }
    };

    return (
      <div className="image-upload-container">
        <label className="image-upload-label">{label}</label>
        <div className="image-preview-wrapper">
          <img 
            src={value} 
            alt="Preview" 
            className="image-preview"
          />
          <div className="image-upload-overlay">
            <label className="image-upload-button">
              Change Image
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="image-upload-input"
              />
            </label>
          </div>
        </div>
      </div>
    );
  };

  // Close modal on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`campaign-modal-overlay ${isOpen ? "show" : "hide"}`}
      onClick={handleOverlayClick}
    >
      <div className="campaign-modal">
        <div className="campaign-modal-header">
          <h2 className="campaign-modal-title">Create Campaign</h2>
          <button 
            className="campaign-modal-close"
            onClick={onClose}
            type="button"
          >
            <IoClose size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="campaign-modal-form">
          <div className="campaign-modal-body">
            
            {/* Title */}
            <div className="form-group">
              <label className="form-label">Campaign Title</label>
              <input
                name="title"
                type="text"
                value={formValues.title}
                onChange={handleInputChange}
                className={`form-input ${validationErrors.title ? "error" : ""}`}
                placeholder="Enter campaign title"
              />
              {validationErrors.title && (
                <div className="form-error">{validationErrors.title}</div>
              )}
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                className={`form-input ${validationErrors.description ? "error" : ""}`}
                placeholder="Describe your campaign"
                rows="4"
              />
              {validationErrors.description && (
                <div className="form-error">{validationErrors.description}</div>
              )}
            </div>

            {/* Community Select Dropdown */}
            <div className="form-group">
              <label className="form-label">Select Community</label>
              <div className="community-select-wrapper">
                {communityLoader ? (
                  <div className="community-loader">
                    <FiLoader className="spinner" />
                    <span>Loading communities...</span>
                  </div>
                ) : (
                  <>
                    <select
                      name="communityId"
                      value={formValues.communityId}
                      onChange={(e) => {
                        const communityId = e.target.value;
                        console.log(communityId);
                        
                        const community = communityOptions.find(c => c.id === communityId);
                        if (community) {
                          handleCommunityChange(community);
                        }
                      }}
                      className={`form-input ${validationErrors.communityId ? "error" : ""}`}
                      disabled={communityLoader}
                    >
                      <option value="">Select a community</option>
                      {communityOptions.map((community) => (
                        <option 
                          key={community.id} 
                          value={community.id}
                          style={{ paddingLeft: `${community.level * 20}px` }}
                        >
                          {community.displayName}
                        </option>
                      ))}
                    </select>
                    {communityOptions.length === 0 && !communityLoader && (
                      <div className="form-hint">
                        No communities found. Create a community first.
                      </div>
                    )}
                  </>
                )}
                {validationErrors.communityId && (
                  <div className="form-error">{validationErrors.communityId}</div>
                )}
              </div>
            </div>

            {/* End Date */}
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                name="endDate"
                min={new Date().toISOString().split("T")[0]}
                value={formValues.endDate}
                onChange={handleInputChange}
                className={`form-input ${validationErrors.endDate ? "error" : ""}`}
              />
              {validationErrors.endDate && (
                <div className="form-error">{validationErrors.endDate}</div>
              )}
            </div>

            {/* Amount Per User */}
            <div className="form-group">
              <label className="form-label">Amount Per User</label>
              <div className="amount-input-group">
                <select
                  onChange={handleInputChange}
                  className="currency-select"
                  name="currency"
                  value={formValues.currency}
                >
                  <option value="₹">₹</option>
                  <option value="$">$</option>
                  <option value="€">€</option>
                  <option value="£">£</option>
                </select>
                <input
                  type="number"
                  name="amountPerUser"
                  value={formValues.amountPerUser}
                  onChange={handleInputChange}
                  className={`form-input ${validationErrors.amountPerUser ? "error" : ""}`}
                  placeholder="1.00"
                  min="1"
                  step="0.01"
                />
              </div>
              {validationErrors.amountPerUser && (
                <div className="form-error">{validationErrors.amountPerUser}</div>
              )}
            </div>

            {/* Target Fundraise (Read-only) */}
            <div className="form-group">
              <label className="form-label">Target Fundraise</label>
              <input
                type="text"
                name="totalAmountNeeded"
                className="form-input"
                value={`${formValues.currency} ${formValues.totalAmountNeeded.toFixed(2)}`}
                readOnly
              />
              <div className="form-hint">
                Calculated: {formValues.amountPerUser} × {formValues.totalMembers} members
              </div>
            </div>

            {/* Schedule */}
            <div className="form-group">
              <label className="form-label">Schedule</label>
              <select
                onChange={handleInputChange}
                className="form-input"
                name="schedule"
                value={formValues.schedule}
              >
                {scheduleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tax Section - Multiple Selection */}
            <div className="form-group">
              <label className="form-label">Select Tax Types</label>
              <div className="tax-options-container">
                {taxTypeOptions.map((option) => {
                  const isSelected = isTaxTypeSelected(option.value);
                  const percentage = getTaxPercentage(option.value);
                  
                  return (
                    <div key={option.value} className="tax-option-row">
                      <div className="tax-checkbox-container">
                        <input
                          type="checkbox"
                          id={`taxType-${option.value}`}
                          checked={isSelected}
                          onChange={() => handleTaxTypeChange(option.value)}
                        />
                        <label htmlFor={`taxType-${option.value}`}>
                          {option.label}
                        </label>
                      </div>
                      
                      {/* Percentage input - only enabled when tax type is selected */}
                      <div className={`tax-percentage-input ${isSelected ? 'enabled' : 'disabled'}`}>
                        <input
                          type="number"
                          placeholder="%"
                          value={percentage}
                          onChange={(e) => handleTaxPercentageChange(option.value, e.target.value)}
                          disabled={!isSelected}
                          min="0"
                          max="100"
                          step="0.01"
                          className={`percentage-input ${validationErrors[`tax_${option.value}`] ? "error" : ""}`}
                        />
                        <span className="percentage-symbol">%</span>
                      </div>
                      
                      {validationErrors[`tax_${option.value}`] && (
                        <div className="form-error tax-error">
                          {validationErrors[`tax_${option.value}`]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Display total tax percentage */}
              {formValues.taxes.length > 0 && (
                <div className="total-tax-section">
                  <div className="form-label">Selected Taxes:</div>
                  <div className="selected-taxes-list">
                    {formValues.taxes.map(tax => (
                      <div key={tax.type} className="selected-tax-item">
                        <span className="tax-type">{taxTypeOptions.find(t => t.value === tax.type)?.label}</span>
                        <span className="tax-percentage">{tax.percentage}%</span>
                      </div>
                    ))}
                    <div className="total-tax-amount">
                      Total Tax: {formValues.taxes.reduce((sum, tax) => sum + (parseFloat(tax.percentage) || 0), 0).toFixed(2)}%
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cover Image Upload */}
            <ImageUpload
              label="Cover Image"
              fieldName="coverImage"
              value={coverImagePreview || DEFAULT_CAMPAIGN_IMAGE}
              onChange={handleImageUpload}
            />

          </div>
          
          <div className="campaign-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loader}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loader || communityLoader || !formValues.communityId}
            >
              {loader ? (
                <>
                  <FiLoader className="spinner" />
                  Creating...
                </>
              ) : "Create Campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignSetupModal;